import {
    FormControl,
    FormHelperText,
    Grid2 as Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material"
import { ReactNode } from "react"
import { WidthOpt } from "../../assets/inputStyles.tsx"
import { useFieldsStyles } from "./FieldsStyles"

interface NexSelectProps<T extends string> {
    id?: string
    label?: string
    value: T
    onChange: (event: SelectChangeEvent<T>, child: ReactNode) => void
    options: readonly string[]
    displayValues?: string[]
    disabled?: boolean
    disableDefaultValue?: boolean
    fullWidth?: boolean
    width?: WidthOpt
    gutterBottom?: WidthOpt
    menuHeight?: WidthOpt
    inline?: boolean
    disableGutter?: boolean
    helperText?: string
    error?: boolean
    className?: string
    displayEmpty?: boolean
    MenuProps?: any
}

export const NexSelect = <T extends string>({
    id,
    label,
    value,
    onChange,
    options,
    displayValues,
    disabled,
    disableDefaultValue,
    fullWidth,
    width = "sm",
    gutterBottom = "md",
    disableGutter,
    menuHeight = "sm",
    helperText,
    error,
    inline,
    className,
    ...props
}: NexSelectProps<T>) => {
    const { classes: styles, cx } = useFieldsStyles()

    return (
        <Grid
            className={cx(
                {
                    [styles[`gutterBottom__${gutterBottom}`]]: !disableGutter,
                    [styles.inline]: inline,
                },
                className,
            )}
        >
            <FormControl
                variant="outlined"
                fullWidth={fullWidth}
                error={error}
                disabled={disabled}
                className={cx({ [styles[`input__${width}`]]: !fullWidth })}
            >
                <InputLabel id={id && `${id}-select-label`}>{label}</InputLabel>
                <Select
                    labelId={id && `${id}-select-label`}
                    id={id && `${id}-select`}
                    value={value}
                    onChange={onChange}
                    label={label}
                    className={cx(styles.select, { [styles.placeholder]: value === "" })}
                    MenuProps={{
                        classes: {
                            paper: styles[`menuHeight__${menuHeight}`],
                        },
                        anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                        },
                        transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                        },
                    }}
                    inputProps={{ "data-testid": id && `${id}-select-input` }}
                    {...props}
                >
                    {!disableDefaultValue && (
                        <MenuItem value="" className={styles.placeholder}>
                            Select...
                        </MenuItem>
                    )}
                    {options.map((option, index) => {
                        return (
                            <MenuItem key={option} value={option}>
                                {displayValues ? displayValues[index] : option}
                            </MenuItem>
                        )
                    })}
                </Select>
                {helperText && <FormHelperText>{helperText}</FormHelperText>}
            </FormControl>
        </Grid>
    )
}
